import { afterNextRender, Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Store } from '@ngrx/store';
import {
  CheckoutExtensionCommonConfig,
  CheckoutExtensionUI,
  CheckoutUpsellExtension,
  ExtensionComponentType,
  GiftUnlockExtension,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ViewMode } from '../../../models';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { generateRandomString } from '@utils/string-utils';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ExtensionsService } from '@app/pages/checkout-app/services/extensions/extensions.service';
import { maxxAiRangeValidator } from './gift-unlock.validators';
import type { GiftUnlockOfferComponent } from './components/gift-unlock-offer/gift-unlock-offer.component';
import PlacementMode = CheckoutExtensionCommonConfig.PlacementMode;

export enum GiftUnlockPreviewMode {
  GiftBanner = 'giftBanner',
  GiftPopup = 'giftPopup',
  MissingInfoPopup = 'missingInfoPopup',
  GiftOffer = 'giftOffer',
}

enum GiftUnlockTab {
  SETTINGS = 'settings',
  GIFT_MESSAGE = 'gift_message',
  OFFER = 'offer',
}

export enum GiftUnlockMode {
  MANUAL = 'manual',
  MAXX_AI = 'maxx_ai',
}

@Component({
  selector: 'gift-unlock',
  templateUrl: './gift-unlock.component.html',
  styleUrls: ['./gift-unlock.component.scss'],
  standalone: false,
})
export class GiftUnlockComponent implements OnInit, OnDestroy {
  @ViewChild('giftUnlockOffer') giftUnlockOffer: GiftUnlockOfferComponent;

  @onDestroyComplete() destroy$ = new Subject();

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  formGroup: UntypedFormGroup;
  defaultIconUrl = `${window.location.origin}/assets/icons/gift_icon.png`;
  offerTypeOptions = [
    { label: 'Product', value: GiftUnlockExtension.OfferType.PRODUCT },
    { label: 'Selling plan product', value: GiftUnlockExtension.OfferType.SELLING_PLAN_PRODUCT },
    { label: 'Coupon', value: GiftUnlockExtension.OfferType.COUPON },
  ];
  couponTypeOptions = [
    { label: 'Fixed amount', value: GiftUnlockExtension.CouponType.FIXED_AMOUNT },
    { label: 'Percentage', value: GiftUnlockExtension.CouponType.PERCENTAGE },
  ];

  tabs = [
    { label: 'Settings', value: GiftUnlockTab.SETTINGS },
    { label: 'Gift Message', value: GiftUnlockTab.GIFT_MESSAGE },
    { label: 'Offer', value: GiftUnlockTab.OFFER },
  ];
  GiftUnlockTab = GiftUnlockTab;
  activeTab = GiftUnlockTab.SETTINGS;
  popupActiveTab = 'giftPopup';
  displayRules = getExtensionRules();
  displayRulesEntities;
  updateRequired;
  updateAppUrl;
  activePreviewMode: GiftUnlockPreviewMode = GiftUnlockPreviewMode.GiftBanner;
  PreviewMode = GiftUnlockPreviewMode;
  OfferType = GiftUnlockExtension.OfferType;
  CouponType = GiftUnlockExtension.CouponType;
  PlacementMode = PlacementMode;
  displayDesignConfigurationSections = true;
  isLegacyHideInCartLevel = false;
  shopCurrencySymbol: string;
  requiredFieldMessage = 'Required field';

  constructor(
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    private shopService: ShopService,
    private extensionsService: ExtensionsService,
    private injector: Injector,
  ) {}
  ngOnDestroy() {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.GIFT_UNLOCK);

    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.displayRulesEntities = this.displayRules.map((r) => r.entity);
    this.initForm();
    this.shopService.getUrlToUpdateAppIfRequired(['write_discounts']).subscribe((res) => {
      this.updateRequired = res.requireUpdate;
      this.updateAppUrl = res.updateUrl;
    });
    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension.template, extension.usageToken);
        this.migrateLegacyProduct(extension.template);
      }
    });
  }

  get isGiftMessageTabDisabled(): boolean {
    return !!this.formGroup?.get('settings.productDetails.autoAddToCart')?.value;
  }

  onTabChange(event: GiftUnlockTab) {
    this.activeTab = event;
    if (event === GiftUnlockTab.GIFT_MESSAGE) {
      this.onPreviewModeChange(GiftUnlockPreviewMode.GiftBanner);
    } else if (event === GiftUnlockTab.OFFER) {
      this.onPreviewModeChange(this.resolveOfferPreviewMode());
    }
  }

  onReviewHideInCartLevel(): void {
    this.onTabChange(GiftUnlockTab.OFFER);
    afterNextRender(() => this.giftUnlockOffer?.scrollToHideInCartLevel(), { injector: this.injector });
  }

  get isGiftBannerPreviewActive(): boolean {
    return this.activePreviewMode === GiftUnlockPreviewMode.GiftBanner;
  }

  dispatchExampleViewValues(values) {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      isModal:
        this.activePreviewMode !== GiftUnlockPreviewMode.GiftBanner &&
        this.activePreviewMode !== GiftUnlockPreviewMode.GiftOffer,
      [ExtensionComponentType.GIFT_UNLOCK]: {
        mode,
        previewMode: this.activePreviewMode,
        template: { ...values },
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, usageToken?: string) {
    const loadedProductDetails = loadedValues?.settings?.productDetails;
    this.isLegacyHideInCartLevel =
      !!loadedProductDetails?.hideIfProductIsInCart && loadedProductDetails?.hideIfProductIsInCartLevel === undefined;
    this.formGroup = new UntypedFormGroup({
      usageToken: new UntypedFormControl(usageToken ?? 'gift_unlock_' + generateRandomString(12)),
      internalName: new UntypedFormControl(loadedValues?.internalName ?? 'Gift unlock', Validators.required),
      title: new UntypedFormControl(loadedValues?.title ?? 'Gift unlock'),
      placementMode: new UntypedFormControl(loadedValues?.placementMode ?? PlacementMode.STANDALONE),
      settings: new UntypedFormGroup({
        mode: new UntypedFormControl(loadedValues?.settings?.mode ?? GiftUnlockMode.MANUAL),
        offerType: new UntypedFormControl(loadedValues?.settings?.offerType ?? GiftUnlockExtension.OfferType.PRODUCT),
      }),
      displayRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.displayRules ?? [],
        this.displayRules,
      ),
      keepInCart: new UntypedFormControl(loadedValues?.keepInCart ?? false),
      giftMessage: new UntypedFormGroup({
        enabled: new UntypedFormControl(loadedValues?.giftMessage?.enabled ?? true),
        title: new UntypedFormControl(loadedValues?.giftMessage?.title ?? `There's a gift waiting for you!`),
        text: new UntypedFormControl(loadedValues?.giftMessage?.text ?? 'Go through checkout to claim it'),
        actionButtonText: new UntypedFormControl(loadedValues?.giftMessage?.actionButtonText ?? 'See gift'),
        buttonAppearance: new UntypedFormControl(
          loadedValues?.giftMessage?.buttonAppearance ?? CheckoutUpsellExtension.ButtonAppearance.MONOCHROME,
        ),
        icon: new UntypedFormControl(loadedValues?.giftMessage?.icon ?? this.defaultIconUrl),
      }),
      giftMessageStyle: new UntypedFormGroup({
        border: new UntypedFormControl(loadedValues?.giftMessageStyle?.border ?? CheckoutExtensionUI.BorderType.NONE),
        borderWidth: new UntypedFormControl(
          loadedValues?.giftMessageStyle?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE,
        ),
        cornerRadius: new UntypedFormControl(
          loadedValues?.giftMessageStyle?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE,
        ),
        padding: new UntypedFormControl(loadedValues?.giftMessageStyle?.padding ?? CheckoutExtensionUI.Padding.BASE),
      }),
      giftPopup: new UntypedFormGroup({
        title: new UntypedFormControl(loadedValues?.giftPopup?.title ?? `Here's your gift`),
        text: new UntypedFormControl(
          loadedValues?.giftPopup?.text ?? 'Click the button below to add it to your shopping cart',
        ),
        actionButtonText: new UntypedFormControl(loadedValues?.giftPopup?.actionButtonText ?? 'I want this!'),
        declineButtonText: new UntypedFormControl(loadedValues?.giftPopup?.declineButtonText ?? 'No thank you'),
      }),
      missingInformationPopup: new UntypedFormGroup({
        title: new UntypedFormControl(loadedValues?.missingInformationPopup?.title ?? `Not just yet`),
        text: new UntypedFormControl(
          loadedValues?.missingInformationPopup?.text ?? 'You need to put in your information first',
        ),
        dismissButtonText: new UntypedFormControl(loadedValues?.missingInformationPopup?.dismissButtonText ?? 'Okay'),
      }),
      offer: new UntypedFormGroup({
        giftMode: new UntypedFormControl(loadedValues?.offer?.giftMode ?? GiftUnlockExtension.GiftMode.SINGLE),
        products: new UntypedFormControl(loadedValues?.offer?.products ?? []),
        displayAsPopUp: new UntypedFormControl(loadedValues?.offer?.displayAsPopUp ?? true),
        productDisplayType: new UntypedFormControl(
          loadedValues?.offer?.productDisplayType ?? GiftUnlockExtension.ProductDisplayType.COMPACT,
        ),
        variantSelector: new UntypedFormControl(
          loadedValues?.offer?.variantSelector ?? GiftUnlockExtension.VariantSelectorType.INSIDE,
        ),
        buttonTextForVariants: new UntypedFormControl(loadedValues?.offer?.buttonTextForVariants ?? 'Select options'),
        buttonAppearance: new UntypedFormControl(
          loadedValues?.offer?.buttonAppearance ?? CheckoutUpsellExtension.ButtonAppearance.MONOCHROME,
        ),
        declineButtonEnabled: new UntypedFormControl(loadedValues?.offer?.declineButtonEnabled ?? true),
      }),
      offerStyle: new UntypedFormGroup({
        mainBackground: new UntypedFormGroup({
          border: new UntypedFormControl(
            loadedValues?.offerStyle?.mainBackground?.border ?? CheckoutExtensionUI.BorderType.BASE,
          ),
          borderWidth: new UntypedFormControl(
            loadedValues?.offerStyle?.mainBackground?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE,
          ),
          cornerRadius: new UntypedFormControl(
            loadedValues?.offerStyle?.mainBackground?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.BASE,
          ),
          padding: new UntypedFormControl(
            loadedValues?.offerStyle?.mainBackground?.padding ?? CheckoutExtensionUI.Padding.BASE,
          ),
        }),
        image: new UntypedFormGroup({
          border: new UntypedFormControl(
            loadedValues?.offerStyle?.image?.border ?? CheckoutExtensionUI.BorderType.NONE,
          ),
          borderWidth: new UntypedFormControl(
            loadedValues?.offerStyle?.image?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE,
          ),
          cornerRadius: new UntypedFormControl(
            loadedValues?.offerStyle?.image?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE,
          ),
          padding: new UntypedFormControl(loadedValues?.offerStyle?.image?.padding ?? CheckoutExtensionUI.Padding.NONE),
        }),
      }),
    });

    if (
      this.formGroup.get('settings.offerType').value === GiftUnlockExtension.OfferType.PRODUCT ||
      this.formGroup.get('settings.offerType').value === GiftUnlockExtension.OfferType.SELLING_PLAN_PRODUCT
    ) {
      (this.formGroup.get('settings') as UntypedFormGroup).addControl(
        'productDetails',
        this.createProductDetailsFormGroup(loadedValues),
      );
    } else {
      (this.formGroup.get('settings') as UntypedFormGroup).addControl(
        'couponDetails',
        this.createCouponDetailsFormGroup(loadedValues),
      );
    }

    this.formGroup
      .get('settings.offerType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (
          value === GiftUnlockExtension.OfferType.PRODUCT ||
          value === GiftUnlockExtension.OfferType.SELLING_PLAN_PRODUCT
        ) {
          (this.formGroup.get('settings') as UntypedFormGroup).removeControl('couponDetails');
          (this.formGroup.get('settings') as UntypedFormGroup).addControl(
            'productDetails',
            this.createProductDetailsFormGroup(),
          );

          const sellingPlanControl = this.formGroup.get('settings.productDetails.sellingPlanId');
          if (sellingPlanControl) {
            if (value === GiftUnlockExtension.OfferType.SELLING_PLAN_PRODUCT) {
              sellingPlanControl.setValidators([Validators.required]);
            } else {
              sellingPlanControl.clearValidators();
            }
            sellingPlanControl.updateValueAndValidity();
          }

          const variantIdControl = this.formGroup.get('settings.productDetails.variantId');
          if (variantIdControl) {
            if (value === GiftUnlockExtension.OfferType.SELLING_PLAN_PRODUCT) {
              variantIdControl.setValidators([Validators.required]);
            } else {
              variantIdControl.clearValidators();
            }
            variantIdControl.updateValueAndValidity();
          }
        } else {
          (this.formGroup.get('settings') as UntypedFormGroup).removeControl('productDetails');
          (this.formGroup.get('settings') as UntypedFormGroup).addControl(
            'couponDetails',
            this.createCouponDetailsFormGroup(),
          );
        }
      });

    const modeControl = this.formGroup.get('settings.mode');
    if (modeControl) {
      const lockOfferTypeWhenMaxxAi = (mode: GiftUnlockMode, emitEvent: boolean) => {
        if (mode === GiftUnlockMode.MAXX_AI) {
          this.formGroup.get('settings.offerType')?.setValue(GiftUnlockExtension.OfferType.COUPON, { emitEvent });
        }
      };

      modeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((mode) => {
        lockOfferTypeWhenMaxxAi(mode, true);
        this.formGroup.get('settings.couponDetails.maxxAi')?.updateValueAndValidity();
      });

      lockOfferTypeWhenMaxxAi(modeControl.value, false);
      this.formGroup.get('settings.couponDetails.maxxAi')?.updateValueAndValidity();
    }

    this.dispatchExampleViewValues(this.formGroup.value);
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.displayDesignConfigurationSections = !values.settings.productDetails?.autoAddToCart;
      if (values.settings.productDetails?.autoAddToCart) {
        this.onPreviewModeChange(GiftUnlockPreviewMode.GiftBanner);
        if (this.activeTab === GiftUnlockTab.GIFT_MESSAGE) {
          this.activeTab = GiftUnlockTab.SETTINGS;
        }
        if (values.offer?.giftMode === GiftUnlockExtension.GiftMode.MULTIPLE) {
          this.formGroup.get('offer.giftMode')?.setValue(GiftUnlockExtension.GiftMode.SINGLE);
          this.formGroup.get('offer.products')?.setValue([]);
        }
      }

      if (!this.isGiftBannerPreviewActive) {
        this.activePreviewMode = this.resolveOfferPreviewMode();
      }

      this.dispatchExampleViewValues(values);
      this.extensionsService.setFormValid(this.isFormValid);
    });

    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.isFormValid);
    });
    this.extensionsService.setFormValid(this.isFormValid);
  }

  createProductDetailsFormGroup(loadedValues?) {
    return new UntypedFormGroup({
      variantId: new UntypedFormControl(loadedValues?.settings?.productDetails?.variantId ?? ''),
      sellingPlanId: new UntypedFormControl(loadedValues?.settings?.productDetails?.sellingPlanId ?? ''),
      hideOfferDetails: new UntypedFormControl(loadedValues?.settings?.productDetails?.hideOfferDetails ?? false),
      hideIfProductIsInCart: new UntypedFormControl(
        loadedValues?.settings?.productDetails?.hideIfProductIsInCart ?? false,
      ),
      hideIfProductIsInCartLevel: new UntypedFormControl(
        loadedValues?.settings?.productDetails?.hideIfProductIsInCartLevel ??
          GiftUnlockExtension.HideInCartLevel.PARENT_PRODUCT,
      ),
      autoAddToCart: new UntypedFormControl(loadedValues?.settings?.productDetails?.autoAddToCart ?? false),
      discountConfiguration: new UntypedFormGroup({
        enabled: new UntypedFormControl(
          loadedValues?.settings?.productDetails?.discountConfiguration?.enabled ?? false,
        ),
        discountValue: new UntypedFormControl(
          loadedValues?.settings?.productDetails?.discountConfiguration?.discountValue ?? 0,
        ),
        discountType: new UntypedFormControl(
          loadedValues?.settings?.productDetails?.discountConfiguration?.discountType ??
            GiftUnlockExtension.CouponType.FIXED_AMOUNT,
        ),
        label: new UntypedFormControl(loadedValues?.settings?.productDetails?.discountConfiguration?.label ?? ''),
      }),
    });
  }

  createCouponDetailsFormGroup(loadedValues?) {
    return new UntypedFormGroup({
      couponType: new UntypedFormControl(
        loadedValues?.settings?.couponDetails?.couponType ?? GiftUnlockExtension.CouponType.FIXED_AMOUNT,
      ),
      discountValue: new UntypedFormControl(loadedValues?.settings?.couponDetails?.discountValue ?? 0),
      couponExpiryDays: new UntypedFormControl(loadedValues?.settings?.couponDetails?.couponExpiryDays ?? null),
      autoApplyCoupon: new UntypedFormControl(loadedValues?.settings?.couponDetails?.autoApplyCoupon ?? false),
      maxxAi: new UntypedFormGroup(
        {
          min: new UntypedFormControl(loadedValues?.settings?.couponDetails?.maxxAi?.min ?? null, [Validators.min(1)]),
          max: new UntypedFormControl(loadedValues?.settings?.couponDetails?.maxxAi?.max ?? null),
          allowZero: new UntypedFormControl(loadedValues?.settings?.couponDetails?.maxxAi?.allowZero ?? false),
        },
        { validators: maxxAiRangeValidator },
      ),
      combinesWithProductDiscounts: new UntypedFormControl(
        loadedValues?.settings?.couponDetails?.combinesWithProductDiscounts ?? false,
      ),
      combinesWithOrderDiscounts: new UntypedFormControl(
        loadedValues?.settings?.couponDetails?.combinesWithOrderDiscounts ?? false,
      ),
      combinesWithShippingDiscounts: new UntypedFormControl(
        loadedValues?.settings?.couponDetails?.combinesWithShippingDiscounts ?? false,
      ),
    });
  }

  onUpdateAppPermissions() {
    window.open(this.updateAppUrl, '_blank');
  }

  onPreviewModeChange(mode: GiftUnlockPreviewMode) {
    this.activePreviewMode = mode;
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  onPopupTabChange(tab: string) {
    this.popupActiveTab = tab;
    if (!this.isGiftBannerPreviewActive) {
      this.onPreviewModeChange(this.resolveOfferPreviewMode());
    }
  }

  onTogglePreviewBetweenGiftBannerAndOffer() {
    this.onPreviewModeChange(
      this.isGiftBannerPreviewActive ? this.resolveOfferPreviewMode() : GiftUnlockPreviewMode.GiftBanner,
    );
  }

  private resolveOfferPreviewMode(): GiftUnlockPreviewMode {
    const giftMessageEnabled = this.formGroup.get('giftMessage.enabled')?.value ?? true;
    if (giftMessageEnabled && this.popupActiveTab === 'missingInformationPopup') {
      return GiftUnlockPreviewMode.MissingInfoPopup;
    }
    const displayAsPopUp = this.formGroup.get('offer.displayAsPopUp')?.value;
    return giftMessageEnabled && displayAsPopUp ? GiftUnlockPreviewMode.GiftPopup : GiftUnlockPreviewMode.GiftOffer;
  }

  private get isFormValid(): boolean {
    if (!this.formGroup?.valid) return false;
    if (this.formGroup.get('settings.offerType')?.value === GiftUnlockExtension.OfferType.PRODUCT) {
      return (this.formGroup.get('offer.products')?.value ?? []).length > 0;
    }
    return true;
  }

  private migrateLegacyProduct(template: any): void {
    const offerType = template?.settings?.offerType;
    const variantId = template?.settings?.productDetails?.variantId;
    const existingProducts = template?.offer?.products;

    if (offerType !== GiftUnlockExtension.OfferType.PRODUCT) return;
    if (existingProducts?.length) return;
    if (!variantId) return;

    this.extensionsService
      .getItemByProductOrVariantId(variantId)
      .pipe(take(1))
      .subscribe((product) => {
        if (product) {
          this.formGroup.get('offer.products')?.setValue([product]);
        }
      });
  }

  handleSave() {
    const formValue = this.formGroup.value;
    const { usageToken, ...template } = formValue;
    const hasCartValueRule = (template.displayRules ?? []).some((rule) => rule?.entity === RuleEntity.CART_VALUE);
    const isCouponOfferType = template.settings?.offerType === GiftUnlockExtension.OfferType.COUPON;
    template.keepInCart = hasCartValueRule && isCouponOfferType ? template.keepInCart === true : false;
    const payload: ICheckoutExtensions = {
      template,
      usageToken,
      type: ExtensionComponentType.GIFT_UNLOCK,
    };
    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...payload, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
}
